'use client';

import React, { createContext, ReactNode, useContext, useEffect, useRef } from 'react';

import { mapProviderToEnum, resetAuthState } from '@/_auth/lib/auth.functions';
import { AuthSession, AuthSessionUser } from '@/_auth/lib/auth.types';
import { AuthFields, AuthStatus, AuthUser } from '@/_auth/interfaces/auth.interfaces';
import { authSetError, authSetProvider, authSetStatus, authSetUser } from '@/_auth/redux/auth/auth.slices';
import { authClient } from '@/_auth/utils/auth/client';
import { AuthorisationFields, CORE_PRODUCT } from '@/interfaces/authorisation/authorisation.interfaces';
import { authorisationLoadAuthorisation } from '@/redux/authorisation/authorisation.slices';
import { formatErrorMessage, resetUserState } from '@/lib/functions';
import { LogType, logTransaction } from '@/lib/logTransaction';
import { devConsole } from '@/lib/devConsole';
import { useAppDispatch } from '@/redux/store.hooks';
import { userGetUserByEmail } from '@/redux/user/user.thunk';

const NAMESPACE = 'AuthProvider';

interface AuthProviderState {
  error: string | null;
  initialized: boolean;
  ready: boolean;
  session: AuthSession | null;
}

interface AuthProviderProps {
  children: ReactNode;
}

const initialState: AuthProviderState = {
  error: null,
  initialized: false,
  ready: false,
  session: null,
};

export const authProvider = {
  state: initialState,
  actions: {} as {
    initSession: () => Promise<AuthSession | null>;
    refreshSession: () => Promise<AuthSession | null>;
    signOut: () => Promise<void>;
  },
};

export const AuthContext = createContext<typeof authProvider>(authProvider);

export const useAuth = () => useContext(AuthContext);

const refreshSessionStatic = async (): Promise<AuthSession | null> => {
  logTransaction(LogType.Internal, 'refreshSession called');

  const { data, error } = await authClient.auth.refreshSession();

  if (error || !data.session) {
    logTransaction(LogType.Error, 'refreshSession failed', { error: error?.message });
    return null;
  }

  logTransaction(LogType.Internal, 'refreshSession completed successfully');
  return data.session;
};

const signOutStatic = async (): Promise<void> => {
  logTransaction(LogType.Internal, 'signOut called');
  const { error } = await authClient.auth.signOut();

  if (error) {
    logTransaction(LogType.Error, 'signOut failed', { error: error.message });
    throw new Error(error.message);
  }

  logTransaction(LogType.Internal, 'signOut completed successfully');
};

export const authStatic = {
  actions: {
    refreshSession: refreshSessionStatic,
    signOut: signOutStatic,
  },
};

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = React.useState<AuthProviderState>(initialState);
  const initStarted = useRef(false);
  const dispatch = useAppDispatch();

  // Build the auth user shown in the UI directly from the Entra-issued session. There is no local profile store — identity (name/email)
  // comes from the session token claims.

  const buildAuthUser = (sessionUser: AuthSessionUser): AuthUser => ({
    [AuthFields.AvatarUrl]: null,
    [AuthFields.CreatedAt]: null,
    [AuthFields.Email]: sessionUser.email ?? '',
    [AuthFields.FirstName]: sessionUser.firstName,
    [AuthFields.FullName]: sessionUser.fullName,
    [AuthFields.Id]: sessionUser.id,
    [AuthFields.LastName]: sessionUser.lastName,
    [AuthFields.UpdatedAt]: null,
  });

  const resetState = async () => {
    await resetAuthState(dispatch);
    await resetUserState(dispatch);
    setState((current) => ({
      ...current,
      error: null,
      ready: false,
      session: null,
    }));
  };

  const loadSession = async (): Promise<AuthSession | null> => {
    logTransaction(LogType.Internal, 'loadSession called');
    dispatch(authSetError(null));

    try {
      const { data: sessionData, error: sessionError } = await authClient.auth.getSession();

      if (sessionError || !sessionData.session?.user) {
        await resetState();
        setState({
          error: sessionError?.message ?? null,
          initialized: true,
          ready: false,
          session: null,
        });
        return null;
      }

      dispatch(authSetProvider(mapProviderToEnum(sessionData.session.user.app_metadata.provider)));
      dispatch(authSetStatus(AuthStatus.Authenticated));

      // Identity comes from the Entra session (MFA is enforced by Entra, so there is no app-side factor check).

      dispatch(authSetUser(buildAuthUser(sessionData.session.user)));

      const userEmail = sessionData.session.user.email;
      if (!userEmail) {
        throw new Error(`Session user email is missing, can't fetch user state`);
      }

      await dispatch(userGetUserByEmail({ email: userEmail, product: CORE_PRODUCT })).unwrap();

      // Load the current role into authorisation state from the Entra-issued session (the App Role claim). This is the single source of
      // truth for what the UI may show and the server will allow.

      dispatch(
        authorisationLoadAuthorisation({
          [AuthorisationFields.Active]: true,
          [AuthorisationFields.CreatedAt]: null,
          [AuthorisationFields.Email]: userEmail,
          [AuthorisationFields.Id]: 0,
          [AuthorisationFields.Role]: sessionData.session.user.role,
          [AuthorisationFields.UpdatedAt]: null,
        }),
      );

      setState({
        error: null,
        initialized: true,
        ready: true,
        session: sessionData.session,
      });

      logTransaction(LogType.Internal, 'loadSession completed successfully', {
        userId: sessionData.session.user.id,
      });

      return sessionData.session;
    } catch (error) {
      const message = formatErrorMessage(error) ?? 'Failed to load session';
      devConsole.error(NAMESPACE, `loadSession`, `Error: ${message}`);
      logTransaction(LogType.Error, 'loadSession failed', { error: message });
      await resetState();
      setState({
        error: message,
        initialized: true,
        ready: false,
        session: null,
      });
      return null;
    }
  };

  const handleAuthChange = async (event: string) => {
    dispatch(authSetError(null));

    switch (event) {
      case 'SIGNED_IN':
      case 'TOKEN_REFRESHED':
        try {
          await loadSession();
        } catch (error) {
          devConsole.error(NAMESPACE, `handleAuthChange`, `Error during ${event}: ${formatErrorMessage(error)}`);
          await authClient.auth.signOut();
        }
        break;
      case 'USER_UPDATED':
        try {
          const { data: sessionData } = await authClient.auth.getSession();
          if (sessionData?.session?.user) {
            dispatch(authSetUser(buildAuthUser(sessionData.session.user)));
            setState((current) => ({
              ...current,
              session: sessionData.session,
            }));
          }
        } catch (error) {
          devConsole.error(NAMESPACE, `handleAuthChange`, `Error updating user: ${formatErrorMessage(error)}`);
        }
        break;
      case 'SIGNED_OUT':
        await resetState();
        setState({
          error: null,
          initialized: true,
          ready: false,
          session: null,
        });
        break;
      default:
        break;
    }
  };

  const initSession = async () => loadSession();

  const refreshSession = async () => {
    const session = await refreshSessionStatic();
    if (session) {
      setState((current) => ({
        ...current,
        error: null,
        initialized: true,
        ready: true,
        session,
      }));
    }
    return session;
  };

  const signOut = async () => {
    await signOutStatic();
    await resetState();
    setState({
      error: null,
      initialized: true,
      ready: false,
      session: null,
    });
  };

  useEffect(() => {
    if (initStarted.current) return;
    initStarted.current = true;

    void initSession();

    const {
      data: { subscription },
    } = authClient.auth.onAuthStateChange((event) => {
      void handleAuthChange(event);
    });

    return () => {
      subscription?.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider
      value={{
        state,
        actions: {
          initSession,
          refreshSession,
          signOut,
        },
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
